import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CustomerUsersQueryKeys } from "./keys";
import useApiHelper from "../../hooks/useApiHelper";
import useNotification from "../../components/shared/Notifications/useNotifications";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CreateUpdateUserFormFields } from "../../models/User/CreateUpdateUser";
import { IAddUserMembershipRequest } from "../../models/User/User";

export default function useCustomerUsersMutations(customerId: string) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("User");
  const { post, put, del } = useApiHelper();

  const create = useMutation({
    mutationFn: (user: CreateUpdateUserFormFields) =>
      post(`/customer/${customerId}/users`, user),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerUsersQueryKeys.all(customerId),
      });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const update = useMutation({
    mutationFn: (user: CreateUpdateUserFormFields) =>
      put(`/customer/${customerId}/users/${user.id}`, user),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerUsersQueryKeys.all(customerId),
      });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const setPassword = useMutation({
    mutationFn: (req: { userId: string; password: string }) =>
      put(`/customer/${customerId}/users/${req.userId}/password`, {
        password: req.password,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerUsersQueryKeys.all(customerId),
      });
      addNotification({
        variant: "success",
        primaryText: "Password updated",
      });
    },
    onError: () => {
      addNotification({
        variant: "error",
        primaryText: "Failed to update password",
      });
    },
  });

  const remove = useMutation({
    mutationFn: (userId: string) =>
      del(`/customer/${customerId}/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerUsersQueryKeys.all(customerId),
      });
      deleteHandlers.onSuccess();
    },
    onError: () => {
      deleteHandlers.onError();
    },
  });

  // Adds a user who already exists (for another customer) to this customer.
  const addMembership = useMutation({
    mutationFn: (req: IAddUserMembershipRequest) =>
      post(`/customer/${customerId}/memberships`, req),
    onSuccess: () => {
      // The user now appears here, and their other customers' lists show them as shared.
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      addNotification({
        variant: "success",
        primaryText: "User added to customer",
      });
    },
    onError: (err: Error) => {
      addNotification({
        variant: "error",
        primaryText: "Failed to add user",
        secondaryText: err.message,
      });
    },
  });

  // Removes the user from this customer only; a user with no other customer is deleted.
  const removeMembership = useMutation({
    mutationFn: (userId: string) =>
      del(`/customer/${customerId}/memberships/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      addNotification({
        variant: "success",
        primaryText: "User removed from customer",
      });
    },
    onError: (err: Error) => {
      addNotification({
        variant: "error",
        primaryText: "Failed to remove user",
        secondaryText: err.message,
      });
    },
  });

  const setDefaultMembership = useMutation({
    mutationFn: (userId: string) =>
      put(`/customer/${customerId}/memberships/${userId}/default`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      addNotification({
        variant: "success",
        primaryText: "Default customer updated",
      });
    },
    onError: (err: Error) => {
      addNotification({
        variant: "error",
        primaryText: "Failed to update default customer",
        secondaryText: err.message,
      });
    },
  });

  return {
    create,
    update,
    setPassword,
    remove,
    addMembership,
    removeMembership,
    setDefaultMembership,
  };
}
